import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ILoadingSpinner } from 'src/app/api/modules/core/dynamic/components/ILoadingSpinner';

/**
 * The Loading Spinner Component renders an indeterminate spinner as a
 * placeholder for content that is on its way.
 *
 * It has no size knob and no progress value on purpose. The `<mat-spinner>`
 * element resolves both itself: the selector puts Material's spinner in
 * indeterminate mode, and the unbound diameter falls through to the
 * application-wide `MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS` provided at
 * bootstrap. Binding a diameter here — even conditionally — is what would
 * break that, since Material coerces an absent value to `0` and renders
 * nothing.
 */
@Component({
  selector: 'app-loading-spinner',
  templateUrl: './loading-spinner.component.html',
  styleUrl: './loading-spinner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinner],
})
export class LoadingSpinnerComponent {
  /**
   * The configuration required for this object to function.
   *
   * Unlike the other dynamic components this one renders regardless: a spinner
   * has no content to wait for, and suppressing it until the config lands would
   * only flash the very gap it exists to cover.
   */
  readonly config = input<ILoadingSpinner | null>();

  /**
   * The spinner's accessible name, or `undefined` when the server sent none —
   * which switches the spinner to its decorative treatment in the template.
   */
  protected readonly ariaLabel = computed(() => this.config()?.ariaLabel);
}
